import { useCallback, useEffect, useRef, useState } from 'react'

import { saveAs } from 'file-saver'
import * as THREE from 'three'

import { captureException } from '../../analytics'
import iconSettings from '../../assets/images/icon_settings_black.svg'
import { useAutodeskScript } from '../../hooks/useAutodeskScript'
import { useKnownPosition } from '../../hooks/useKnownPosition'
import { useRealtimePosition } from '../../hooks/useRealtimePosition'
import { useViewer3D } from '../../hooks/useViewer3D'
import {
  applyViewerPositionDelta,
  reverseViewerPositionDelta,
} from '../../utils'
import { ViewerPosition } from '../views/image_viewer/types'

import {
  CapturedBadge,
  CapturedOverlay,
  CheckboxInput,
  CheckboxLabel,
  CheckboxRow,
  HiddenCount,
  MenuButton,
  MenuDescription,
  MenuDivider,
  MenuPanel,
  MenuTitle,
  MenuToggleButton,
  ToggleLabel,
  ToggleRow,
  ToggleSwitch,
} from './ForgeViewerStyles'

//Temporary solution to loading multiple files
const KAYE_FILE_URNS = [
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvQTkwJTIwMjIwMCUyMEtheWVfQ0wlMjBTdWJzdHJ1Y3R1cmUlMjBFeGNhdmF0aW9uJlNob3JpbmclMjBTeXN0ZW1fUjIwMjEucnZ0',
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvQjEwJTIwMjIwMCUyMEtheWVfQ0wlMjBTdGFpcnNfUjIwMjEucnZ0',
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvQjEwJTIwMjIwMCUyMEtheWVfQ0wlMjBTdHJ1Y3R1cmVfUjIwMjEucnZ0',
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvQjIwJTIwMjIwMCUyMEtheWVfQ0wlMjBFeHRlcmlvciUyMEVuY2xvc3VyZV9SMjAyMS5ydnQ=',
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvQzEwJTIwMjIwMCUyMEtheWVfQ0wlMjBJbnRlcmlvcl9SMjAyMS5ydnQ=',
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvQ0wlMjAyMjAwJTIwS2F5ZV9DZW50cmFsLnJ2dA==',
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvRDIwLUQ3MCUyMDIyMDAlMjBLYXllX0NMJTIwQ2VpbGluZyUyMERldmljZXNfUjIwMjEucnZ0',
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvRzIwJTIwMjIwMCUyMEtheWVfQ0wlMjBMYW5kc2NhcGVfUjIwMjEucnZ0',
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvRDMwX0tBWUVfSFZBQ19SMjAyMS5ydnQ=',
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvRDMwX0tBWUVfSFlEUk9OSUNfUjIwMjEucnZ0',
  'urn:dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6ZGlkZ2UvRDIwX0tBWUVfUGx1bWJpbmdfUjIwMjEucnZ0',
]

interface ForgeViewerProps {
  position: THREE.Vector3
  viewerPosition: ViewerPosition
  viewerPositionDelta?: ViewerPosition
  followRealtime?: boolean
  fileUrn: string
  sync: boolean
  initialRotation: number
  onDragging?: (isActive: boolean) => void
  onUpdate?: (newValues: ViewerPosition) => void
  onLoaded: (loaded: boolean) => void
  projectId: string
  onRequestCameraScreenshot?: (callback: (blob: Blob) => void) => void
}

interface ProgressEvent {
  percent: number
  state: number
  model: Record<string, unknown>
}

interface SelectionEvent {
  dbIdArray?: number[]
}

interface FinalFrameEvent {
  value: { finalFrame: boolean }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Autodesk Forge Viewer has no TypeScript definitions
type ForgeNode = any

const toError = (e: unknown): Error =>
  e instanceof Error ? e : new Error(String(e))

const RVT_STABILITY_PROJECT_ID = '33mhdxk2NDtQ'

export const ForgeViewer = ({
  position,
  viewerPosition,
  viewerPositionDelta,
  followRealtime = false,
  fileUrn,
  sync: _sync,
  initialRotation,
  onDragging,
  onUpdate,
  onLoaded,
  projectId,
  onRequestCameraScreenshot,
}: ForgeViewerProps) => {
  const [loaded, setLoaded] = useState(false)
  const { autodeskModule } = useAutodeskScript()
  const { viewer, getViewerPosition, setViewerPosition } = useViewer3D(
    autodeskModule,
    projectId,
  )
  const isMouseDown = useRef<boolean>(false)
  const loadedModelRef = useRef<Record<string, unknown> | null>(null)
  const fileTypeRef = useRef<string | null>(null)
  const { registerCallback, unregisterCallback, updateRealtimePosition } =
    useRealtimePosition()

  const [menuOpen, setMenuOpen] = useState(false)
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const capturedImageRef = useRef<string | null>(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [hideOnClickEnabled, setHideOnClickEnabled] = useState(false)
  const [hiddenDbIds, setHiddenDbIds] = useState<number[]>([])
  const hideOnClickRef = useRef(false)
  const [compositeEnabled, setCompositeEnabled] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const handleUpdate = useCallback(
    (positionToPropagate: ViewerPosition) => {
      if (!isMouseDown.current && !followRealtime) {
        onUpdate && onUpdate(positionToPropagate)
      }
    },
    [followRealtime, onUpdate],
  )

  const handleLookAt = useCallback(
    (positionToLookAt: ViewerPosition) => {
      if (!isMouseDown.current && viewer && loaded) {
        // Verify camera is in valid state before updating
        try {
          const currentPos = viewer.navigation.getPosition()
          const currentTarget = viewer.navigation.getTarget()

          if (!currentPos || !currentTarget) {
            captureException(
              new Error(
                'Cannot update view: Camera position or target is invalid',
              ),
            )
            return
          }

          // Try PanoramaExtension first for pitch/yaw/FOV control
          try {
            setViewerPosition(positionToLookAt)
          } catch {
            // Fallback: Use Forge's native navigation API
            // Update FOV if PanoramaExtension isn't available
            // IMPORTANT: Do NOT set FOV manually for RVT files (per Forge RVT guidelines)
            const camera = viewer.navigation.getCamera()
            if (
              camera &&
              positionToLookAt.hfov &&
              fileTypeRef.current !== 'rvt'
            ) {
              camera.fov = positionToLookAt.hfov
              viewer.navigation.setCamera(camera)
            }
          }
        } catch (error) {
          captureException(toError(error))
        }
      }
    },
    [setViewerPosition, viewer, loaded],
  )

  const { updateKnownPosition } = useKnownPosition(handleLookAt, handleUpdate)

  const handleProgressUpdate = useCallback(
    (e: ProgressEvent) => {
      if (e.percent === 1) {
        // Store reference to loaded model
        if (e.model) {
          loadedModelRef.current = e.model
        }

        viewer.setBimWalkToolPopup(false)
        viewer.setNavigationLock(true)

        const bimWalk = viewer.getExtension('Autodesk.BimWalk')
        if (bimWalk) {
          bimWalk.tool.navigator.configuration.mouseTurnInverted = true
        }

        try {
          viewer.setGroundShadow(false)
          viewer.setGroundReflection(false)
          viewer.setOptimizeNavigation(true)
          viewer.setGhosting(true)

          if (projectId === RVT_STABILITY_PROJECT_ID) {
            // Stability mode for heavy RVT scenes:
            // disable expensive post-processing and progressive refinement.
            viewer.setGhosting(false)
            viewer.setQualityLevel(false, false)
            viewer.setProgressiveRendering(false)

            if (viewer.prefs && typeof viewer.prefs.set === 'function') {
              viewer.prefs.set('ambientShadows', false)
            }
          }
        } catch (e) {
          captureException(toError(e))
        }

        setLoaded(true)
      }
    },
    [projectId, viewer],
  )

  const handleDragging = useCallback(
    (active: boolean) => {
      isMouseDown.current = active
      onDragging && onDragging(active)
    },
    [onDragging],
  )

  const handleEscape = useCallback(() => {
    const camera = viewer.navigation.getCamera()
    let adjusted = position.clone()
    const target = viewer.navigation.getTarget()
    adjusted = rotate(position, target, 90)
    camera.fov = viewerPosition.hfov
    viewer.navigation.setView(position, adjusted)
  }, [position, viewer, viewerPosition])

  const handleFinalFrameRenderedChanged = useCallback(
    (event: FinalFrameEvent) => {
      if (event.value.finalFrame && !isMouseDown.current && !followRealtime) {
        // Per Forge RVT guidelines: Treat RVT like an orbit camera, not a free-fly camera
        const isRvtFile = fileTypeRef.current === 'rvt'

        if (isRvtFile && position && viewer) {
          // Get current camera state after Forge normalization
          const nav = viewer.navigation
          const currentPos = nav.getPosition()
          const currentTarget = nav.getTarget()

          if (currentPos && currentTarget) {
            // Calculate direction vector from current position to desired position
            const desiredPosition = new THREE.Vector3(
              position.x,
              position.y,
              position.z,
            )
            const delta = new THREE.Vector3().subVectors(
              desiredPosition,
              currentPos,
            )
            const deltaLength = delta.length()

            // For RVT, we should NOT move the camera - only adjust the target for rotation
            if (deltaLength < 50) {
              // Very small movement threshold
              // Apply ONLY small target adjustment for rotation (yaw/pitch)
              const adjustedPosition = currentPos
                .clone()
                .add(delta.multiplyScalar(0.3)) // Small position adjustment
              const newTarget = currentTarget
                .clone()
                .add(delta.multiplyScalar(0.1)) // Small target adjustment
              nav.setView(adjustedPosition, newTarget)
            }
          }
        }

        const nativePosition = getViewerPosition()
        updateKnownPosition(nativePosition, true, false)
      }
    },
    [followRealtime, getViewerPosition, updateKnownPosition, position, viewer],
  )

  // Registers itself for realtime updates if mouse is not down
  useEffect(() => {
    const handlePositionUpdate = (pos: ViewerPosition) => {
      if (followRealtime) {
        if (viewerPositionDelta) {
          const actualPosition = applyViewerPositionDelta(
            pos,
            viewerPositionDelta,
          )
          handleLookAt(actualPosition)
        } else {
          handleLookAt(pos)
        }
      }
    }
    registerCallback(handlePositionUpdate)
    return () => {
      unregisterCallback(handlePositionUpdate)
    }
  }, [
    followRealtime,
    handleLookAt,
    registerCallback,
    unregisterCallback,
    viewerPositionDelta,
  ])

  // Listen to Autodesk viewer events
  useEffect(() => {
    if (autodeskModule && viewer) {
      const Autodesk = autodeskModule
      viewer.addEventListener(
        Autodesk.Viewing.PROGRESS_UPDATE_EVENT,
        handleProgressUpdate,
      )
      viewer.addEventListener(Autodesk.Viewing.ESCAPE_EVENT, handleEscape)
      viewer.addEventListener(
        Autodesk.Viewing.FINAL_FRAME_RENDERED_CHANGED_EVENT,
        handleFinalFrameRenderedChanged,
      )
      return () => {
        viewer.removeEventListener(
          Autodesk.Viewing.PROGRESS_UPDATE_EVENT,
          handleProgressUpdate,
        )
        viewer.removeEventListener(Autodesk.Viewing.ESCAPE_EVENT, handleEscape)
        viewer.removeEventListener(
          Autodesk.Viewing.FINAL_FRAME_RENDERED_CHANGED_EVENT,
          handleFinalFrameRenderedChanged,
        )
      }
    }
  }, [
    autodeskModule,
    handleEscape,
    handleFinalFrameRenderedChanged,
    handleProgressUpdate,
    viewer,
  ])

  // Keeps track of mouse moves and live-updates
  useEffect(() => {
    if (viewer) {
      const handleMouseDown = (_event: MouseEvent) => {
        handleDragging(true)
      }
      const handleMouseUp = (_event: MouseEvent) => {
        handleDragging(false)
      }
      const handleMouseMove = (_event: MouseEvent) => {
        if (isMouseDown.current) {
          const nativePosition: ViewerPosition = getViewerPosition()
          if (viewerPositionDelta) {
            const actualPosition = reverseViewerPositionDelta(
              nativePosition,
              viewerPositionDelta,
            )
            updateRealtimePosition(actualPosition)
          } else {
            updateRealtimePosition(nativePosition)
          }
        }
      }
      const handleWheel = (event: WheelEvent) => {
        if (capturedImageRef.current || !viewer) return

        event.preventDefault()
        const camera = viewer.navigation.getCamera()
        const currentPosition = getViewerPosition()
        const baseFov =
          currentPosition.hfov && currentPosition.hfov > 0
            ? currentPosition.hfov
            : camera?.fov || 60
        const delta = event.deltaY > 0 ? 1.05 : 0.95
        const newFov = Math.max(10, Math.min(120, baseFov * delta))
        const updatedPosition = { ...currentPosition, hfov: newFov }

        // Keep camera direction stable: only update zoom (hfov), not yaw/pitch.
        setViewerPosition(updatedPosition)

        // Safety fallback if PanoramaTool hasn't been wired yet.
        if (camera) {
          camera.fov = newFov
          viewer.navigation.setCamera(camera)
        }

        if (viewerPositionDelta) {
          const actualPosition = reverseViewerPositionDelta(
            updatedPosition,
            viewerPositionDelta,
          )
          handleUpdate(actualPosition)
        } else {
          handleUpdate(updatedPosition)
        }
      }
      const canvas = viewer.canvas
      canvas.addEventListener('mousedown', handleMouseDown)
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      canvas.addEventListener('wheel', handleWheel, { passive: false })
      return () => {
        canvas.removeEventListener('mousedown', handleMouseDown)
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        canvas.removeEventListener('wheel', handleWheel)
      }
    }
  }, [
    viewer,
    updateRealtimePosition,
    handleDragging,
    getViewerPosition,
    setViewerPosition,
    viewerPositionDelta,
    handleUpdate,
  ])

  // Load Autodesk document into viewer
  useEffect(() => {
    if (viewer && autodeskModule) {
      const Autodesk = autodeskModule

      // Helper function to recursively find geometry nodes when getDefaultGeometry() returns null
      const findGeometryNode = (
        node: ForgeNode,
        depth = 0,
        maxDepth = 5,
      ): ForgeNode => {
        if (depth > maxDepth) return null

        try {
          if (node) {
            const nodeType = node.type
            const nodeRole = node.role

            if (nodeType === 'geometry' && nodeRole === '3d') {
              return node
            }

            if (nodeType === 'geometry' && nodeRole === 'graphics') {
              return node
            }

            if (nodeRole === '3d') {
              return node
            }
          }

          if (node && node.getChildren) {
            const children = node.getChildren()
            if (children && children.length > 0) {
              for (const child of children) {
                try {
                  if (
                    child.useAsDefault &&
                    (child.type === 'geometry' || child.role === '3d')
                  ) {
                    return child
                  }
                } catch (e) {
                  // Continue
                }
              }

              for (const child of children) {
                const found = findGeometryNode(child, depth + 1, maxDepth)
                if (found) return found
              }
            }
          }
        } catch (e) {
          // Continue
        }

        return null
      }

      const handleDocumentSuccess = (viewerDocument: ForgeNode) => {
        const root = viewerDocument.getRoot()

        // Detect file type from document structure
        try {
          let children: ForgeNode[] = []
          try {
            if (root.getChildren && typeof root.getChildren === 'function') {
              children = root.getChildren()
            }
          } catch (e) {
            // Continue
          }

          if (children.length === 0 && root.data && root.data.children) {
            children = Array.isArray(root.data.children)
              ? root.data.children
              : []
          }

          if (children.length === 0 && root.children) {
            children = Array.isArray(root.children) ? root.children : []
          }

          if (children && children.length > 0) {
            for (const child of children) {
              try {
                const childData = child.data || child
                if (childData && childData.inputFileType) {
                  fileTypeRef.current = childData.inputFileType.toLowerCase()
                  break
                }
                const childName = child.name || childData?.name
                if (childName) {
                  const nameLower = childName.toLowerCase()
                  if (nameLower.includes('.rvt')) {
                    fileTypeRef.current = 'rvt'
                    break
                  } else if (nameLower.includes('.nwd')) {
                    fileTypeRef.current = 'nwd'
                    break
                  }
                }
              } catch (e) {
                // Continue
              }
            }
          }

          // If still not detected, try accessing root data directly
          if (!fileTypeRef.current && root.data) {
            const rootData = root.data
            if (rootData.inputFileType) {
              fileTypeRef.current = rootData.inputFileType.toLowerCase()
            }
            if (
              !fileTypeRef.current &&
              rootData.children &&
              Array.isArray(rootData.children)
            ) {
              for (const child of rootData.children) {
                const childData = child.data || child
                if (childData?.inputFileType) {
                  fileTypeRef.current = childData.inputFileType.toLowerCase()
                  break
                }
              }
            }
          }
        } catch (e) {
          captureException(toError(e))
        }

        let defaultModel = root.getDefaultGeometry()

        // If no default geometry found, search for geometry nodes
        if (!defaultModel) {
          defaultModel = findGeometryNode(root)
        }

        if (viewer) {
          if (!defaultModel) {
            captureException(
              new Error('Cannot load document: No geometry found'),
            )
            return
          }

          const loadOptions =
            projectId === '99Vt3MfWNCN7'
              ? {
                  keepCurrentModels: true,
                  globalOffset: { x: 0, y: 0, z: 0 },
                }
              : undefined

          try {
            if (loadOptions) {
              viewer.loadDocumentNode(viewerDocument, defaultModel, loadOptions)
            } else {
              viewer.loadDocumentNode(viewerDocument, defaultModel)
            }
          } catch (error) {
            captureException(toError(error))
          }
        }
      }

      const handleDocumentFailure = (error?: unknown) => {
        const currentUrn =
          projectId === '99Vt3MfWNCN7' ? 'KAYE_FILE_URNS (multiple)' : fileUrn
        captureException(
          new Error(`Failed fetching Forge manifest for URN: ${currentUrn}`),
        )
        if (error) {
          captureException(toError(error))
        }
      }

      if (projectId === '99Vt3MfWNCN7') {
        KAYE_FILE_URNS.forEach(urn => {
          Autodesk.Viewing.Document.load(
            urn,
            handleDocumentSuccess,
            handleDocumentFailure,
          )
        })
      } else {
        Autodesk.Viewing.Document.load(
          fileUrn,
          handleDocumentSuccess,
          handleDocumentFailure,
        )
      }
    }
  }, [autodeskModule, fileUrn, projectId, viewer])

  // Notifies Autodesk document was loaded
  useEffect(() => {
    onLoaded(loaded)
  }, [loaded, onLoaded])

  // Stand at given position in the BIM model
  const hasSetInitialView = useRef(false)
  useEffect(() => {
    if (!viewer || !position || !loaded) return

    const cameraPosition = new THREE.Vector3(position.x, position.y, position.z)
    const currentCameraPosition = viewer.navigation.getPosition()

    // Check if there is a significant change in position
    const positionChange = cameraPosition.distanceTo(currentCameraPosition)

    if (!hasSetInitialView.current) {
      // On first load, set up the view
      try {
        const models = viewer.getVisibleModels()
        // Detect file type
        let isRvtFile = fileTypeRef.current === 'rvt'
        if (!isRvtFile) {
          // Fallback detection methods
          if (
            fileUrn &&
            (fileUrn.toLowerCase().includes('rvt') ||
              fileUrn.toLowerCase().includes('.rvt'))
          ) {
            isRvtFile = true
            fileTypeRef.current = 'rvt'
          }
        }

        if (isRvtFile) {
          viewer.fitToView()
          viewer.navigation.toPerspective()

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (!viewer || !position) return

              const nav = viewer.navigation

              // Capture current camera distance after fitToView normalization
              const currentPos = nav.getPosition()
              const currentTarget = nav.getTarget()
              const lookDistance =
                currentPos && currentTarget
                  ? currentPos.distanceTo(currentTarget)
                  : 10

              // Set correct position and compute target from viewer yaw/pitch
              const desiredPosition = new THREE.Vector3(
                position.x,
                position.y,
                position.z,
              )
              const yawRadians = THREE.MathUtils.degToRad(viewerPosition.yaw)
              const pitchRadians = THREE.MathUtils.degToRad(
                viewerPosition.pitch,
              )
              const direction = new THREE.Vector3(
                Math.cos(pitchRadians) * Math.sin(yawRadians),
                Math.cos(pitchRadians) * Math.cos(yawRadians),
                Math.sin(pitchRadians),
              )
              const targetPosition = desiredPosition
                .clone()
                .add(direction.multiplyScalar(lookDistance))
              nav.setView(desiredPosition, targetPosition)

              // Apply viewing angle via PanoramaTool and sync state
              setViewerPosition(viewerPosition)
              updateKnownPosition(viewerPosition, true, false)
            })
          })
        } else {
          // NWD APPROACH: Calculate look distance based on model size
          let lookDistance = 50 // Default look distance

          if (models && models.length > 0) {
            try {
              const model = models[0]
              const box = model.getBoundingBox()
              if (box) {
                const size = new THREE.Vector3()
                box.getSize(size)
                const maxDimension = Math.max(size.x, size.y, size.z)
                // For NWD files, use 2% of model size (works well)
                lookDistance = Math.max(maxDimension * 0.02, 50)
                if (lookDistance > 500) lookDistance = 500
              }
            } catch (e) {
              captureException(toError(e))
            }
          }

          // Calculate target point based on rotation
          const targetPosition = new THREE.Vector3(
            cameraPosition.x +
              lookDistance * Math.cos((initialRotation * Math.PI) / 180),
            cameraPosition.y +
              lookDistance * Math.sin((initialRotation * Math.PI) / 180),
            cameraPosition.z,
          )

          // Set FOV for NWD (use viewerPosition.hfov)
          const camera = viewer.navigation.getCamera()
          const finalFov = viewerPosition.hfov || 60
          if (camera) {
            camera.fov = finalFov
            viewer.navigation.setCamera(camera)
          }

          // Set view with position and target (this works for NWD)
          viewer.navigation.setView(cameraPosition, targetPosition)
        }

        hasSetInitialView.current = true
      } catch (error) {
        captureException(toError(error))
        // Fallback to just setting position
        viewer.navigation.setPosition(cameraPosition)
      }
    } else if (positionChange > 1.0) {
      // On subsequent updates, just update position
      viewer.navigation.setPosition(cameraPosition)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fileUrn, setViewerPosition, and updateKnownPosition are intentionally omitted to prevent infinite loops
  }, [position, loaded, viewer, initialRotation, viewerPosition])

  const handleCaptureRender = useCallback(() => {
    if (!viewer) return

    if (capturedImage) {
      setCapturedImage(null)
      capturedImageRef.current = null
      return
    }

    setIsCapturing(true)

    if (hideOnClickEnabled) {
      setHideOnClickEnabled(false)
      hideOnClickRef.current = false
      viewer.clearSelection()
    }

    try {
      viewer.impl.invalidate(true, true, true)
    } catch (e) {
      captureException(toError(e))
    }

    const captureFromCanvas = () => {
      try {
        const canvas = viewer.impl.canvas as HTMLCanvasElement | undefined
        if (canvas) {
          const dataUrl = canvas.toDataURL('image/png')
          setCapturedImage(dataUrl)
          capturedImageRef.current = dataUrl
        }
      } catch (e) {
        captureException(toError(e))
      }
      setIsCapturing(false)
    }

    setTimeout(captureFromCanvas, 2000)
  }, [viewer, capturedImage, hideOnClickEnabled])

  const handleDownloadCapture = useCallback(() => {
    if (!capturedImage) return

    setIsDownloading(true)

    if (compositeEnabled && onRequestCameraScreenshot) {
      // Composite mode: combine 3D render with camera image side-by-side
      onRequestCameraScreenshot((cameraBlob: Blob) => {
        const cameraUrl = URL.createObjectURL(cameraBlob)

        const loadImg = (src: string): Promise<HTMLImageElement> =>
          new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = () => resolve(img)
            img.onerror = reject
            img.src = src
          })

        Promise.all([loadImg(capturedImage), loadImg(cameraUrl)])
          .then(([renderImg, cameraImg]) => {
            // Scale both images to the same height for a clean composite
            const targetHeight = Math.max(renderImg.height, cameraImg.height)
            const renderScale = targetHeight / renderImg.height
            const cameraScale = targetHeight / cameraImg.height
            const scaledRenderWidth = renderImg.width * renderScale
            const scaledCameraWidth = cameraImg.width * cameraScale

            const canvas = document.createElement('canvas')
            canvas.width = scaledRenderWidth + scaledCameraWidth
            canvas.height = targetHeight
            const ctx = canvas.getContext('2d')!

            ctx.fillStyle = '#000'
            ctx.fillRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(renderImg, 0, 0, scaledRenderWidth, targetHeight)
            ctx.drawImage(
              cameraImg,
              scaledRenderWidth,
              0,
              scaledCameraWidth,
              targetHeight,
            )

            canvas.toBlob(blob => {
              if (blob) {
                saveAs(blob, `composite-capture-${Date.now()}.png`)
              }
            })
          })
          .catch((e: unknown) => {
            captureException(toError(e))
          })
          .finally(() => {
            URL.revokeObjectURL(cameraUrl)
            setIsDownloading(false)
          })
      })
    } else {
      // Standard mode: download just the 3D render
      fetch(capturedImage)
        .then(res => res.blob())
        .then(blob => {
          saveAs(blob, `3d-render-capture-${Date.now()}.png`)
        })
        .catch((e: unknown) => {
          captureException(toError(e))
        })
        .finally(() => {
          setIsDownloading(false)
        })
    }
  }, [capturedImage, compositeEnabled, onRequestCameraScreenshot])

  const handleToggleHideOnClick = useCallback(() => {
    setHideOnClickEnabled(prev => {
      const next = !prev
      hideOnClickRef.current = next
      if (!next && viewer) {
        viewer.clearSelection()
      }
      return next
    })
  }, [viewer])

  const handleShowAllHidden = useCallback(() => {
    if (!viewer) return
    try {
      viewer.showAll()
      setHiddenDbIds([])
    } catch (e) {
      captureException(toError(e))
    }
  }, [viewer])

  useEffect(() => {
    if (!viewer || !autodeskModule) return

    const Autodesk = autodeskModule
    const handleSelection = (event: SelectionEvent) => {
      if (capturedImageRef.current) return
      if (!hideOnClickRef.current) return

      const dbIds: number[] = event?.dbIdArray || []
      if (dbIds.length === 0) return

      try {
        viewer.hide(dbIds)
        setHiddenDbIds(prev => [...prev, ...dbIds])
        viewer.clearSelection()
      } catch (e) {
        captureException(toError(e))
      }
    }

    viewer.addEventListener(
      Autodesk.Viewing.SELECTION_CHANGED_EVENT,
      handleSelection,
    )

    return () => {
      viewer.removeEventListener(
        Autodesk.Viewing.SELECTION_CHANGED_EVENT,
        handleSelection,
      )
    }
  }, [viewer, autodeskModule])

  // Updates internal viewer position when property value from parent changes
  useEffect(() => {
    // Only update if model is loaded and user isn't dragging
    if (!isMouseDown.current && !followRealtime && loaded && viewer) {
      // Check if viewer still has models loaded before updating position
      const models = viewer.getVisibleModels()
      if (models && models.length > 0) {
        // Verify the model we loaded is still present
        const modelStillLoaded =
          loadedModelRef.current &&
          models.some(
            (m: Record<string, unknown>) => m === loadedModelRef.current,
          )

        if (modelStillLoaded || models.length > 0) {
          // Before updating, verify camera is in a valid state
          try {
            const currentPos = viewer.navigation.getPosition()
            const currentTarget = viewer.navigation.getTarget()

            // If camera position or target is invalid, reset to known good position
            if (
              !currentPos ||
              !currentTarget ||
              isNaN(currentPos.x) ||
              isNaN(currentPos.y) ||
              isNaN(currentPos.z) ||
              isNaN(currentTarget.x) ||
              isNaN(currentTarget.y) ||
              isNaN(currentTarget.z)
            ) {
              captureException(
                new Error('Camera in invalid state, resetting to position'),
              )
              if (position) {
                const cameraPosition = new THREE.Vector3(
                  position.x,
                  position.y,
                  position.z,
                )

                // For RVT files: Use simpler approach - let Forge handle camera normalization
                if (fileTypeRef.current === 'rvt') {
                  viewer.navigation.setPosition(cameraPosition)
                  viewer.fitToView()
                } else {
                  // For NWD files: Use target calculation approach
                  const lookDistance = 10
                  const targetPosition = new THREE.Vector3(
                    cameraPosition.x +
                      lookDistance *
                        Math.cos((initialRotation * Math.PI) / 180),
                    cameraPosition.y +
                      lookDistance *
                        Math.sin((initialRotation * Math.PI) / 180),
                    cameraPosition.z,
                  )
                  viewer.navigation.setView(cameraPosition, targetPosition)
                }
              }
              return
            }

            updateKnownPosition(viewerPosition, false)
          } catch (error) {
            captureException(toError(error))
          }
        } else {
          captureException(
            new Error(
              'Cannot update viewer position: Loaded model no longer present',
            ),
          )
        }
      } else {
        captureException(
          new Error('Cannot update viewer position: No models loaded'),
        )
      }
    }
  }, [
    followRealtime,
    updateKnownPosition,
    viewerPosition,
    loaded,
    viewer,
    position,
    initialRotation,
  ])

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div id="forgeViewer" style={{ width: '100%', height: '100%' }} />

      {capturedImage && (
        <>
          <CapturedOverlay src={capturedImage} alt="Captured 3D render" />
          <CapturedBadge>
            Render Captured — Click settings to clear
          </CapturedBadge>
        </>
      )}

      {loaded && (
        <>
          <MenuToggleButton
            className={menuOpen ? 'active' : ''}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              setMenuOpen(prev => !prev)
            }}
            onDoubleClick={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <img
              src={iconSettings}
              alt="3D Options"
              style={menuOpen ? {} : { filter: 'brightness(0) invert(1)' }}
            />
          </MenuToggleButton>

          {menuOpen && (
            <MenuPanel
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
            >
              <MenuTitle>3D Viewer Options</MenuTitle>

              <MenuDescription>
                Take a static snapshot of the current 3D view
              </MenuDescription>

              <MenuButton
                $active={!!capturedImage}
                onClick={handleCaptureRender}
                disabled={isCapturing}
              >
                {isCapturing
                  ? 'Capturing...'
                  : capturedImage
                    ? 'Clear Captured Render'
                    : 'Capture Render'}
              </MenuButton>

              {capturedImage && (
                <>
                  {onRequestCameraScreenshot && (
                    <CheckboxRow>
                      <CheckboxInput
                        type="checkbox"
                        checked={compositeEnabled}
                        onChange={() => setCompositeEnabled(prev => !prev)}
                      />
                      <CheckboxLabel>Include Camera Image</CheckboxLabel>
                    </CheckboxRow>
                  )}
                  <MenuButton
                    onClick={handleDownloadCapture}
                    disabled={isDownloading}
                  >
                    {isDownloading
                      ? 'Preparing Download...'
                      : compositeEnabled && onRequestCameraScreenshot
                        ? 'Download Composite'
                        : 'Download Capture'}
                  </MenuButton>
                </>
              )}

              <MenuDivider />

              <MenuDescription>
                Click objects in the 3D model to temporarily hide them
              </MenuDescription>

              <ToggleRow
                style={
                  capturedImage ? { opacity: 0.4, pointerEvents: 'none' } : {}
                }
              >
                <ToggleSwitch
                  $active={hideOnClickEnabled}
                  onClick={handleToggleHideOnClick}
                  disabled={!!capturedImage}
                />
                <ToggleLabel>
                  Hide on Click
                  {hiddenDbIds.length > 0 && (
                    <HiddenCount>({hiddenDbIds.length} hidden)</HiddenCount>
                  )}
                </ToggleLabel>
              </ToggleRow>

              {hiddenDbIds.length > 0 && !capturedImage && (
                <MenuButton onClick={handleShowAllHidden}>
                  Show All Hidden Elements
                </MenuButton>
              )}
            </MenuPanel>
          )}
        </>
      )}
    </div>
  )
}

function rotate(center: THREE.Vector3, p: THREE.Vector3, angle: number) {
  angle = angle * (Math.PI / 180)
  const s = Math.sin(angle)
  const c = Math.cos(angle)
  // translate point back to origin:
  p.x -= center.x
  p.y -= center.y
  // rotate point
  const xnew = p.x * c - p.y * s
  const ynew = p.x * s + p.y * c
  // translate point back:
  p.x = xnew + center.x
  p.y = ynew + center.y
  return p
}
